import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { CompanySubscription, SubscriptionPlan } from '@/features/settings/types/settings.types';
import { devtoolsConfig } from '@/lib/stores/secure-storage';
import {
  type PlanTier,
  type SubscriptionState,
  type SubscriptionActions,
  PLAN_LIMITS,
} from './types';
import {
  getPlanTierFromSubscription,
  canCreateShop as checkCanCreateShop,
  canCreateProduct as checkCanCreateProduct,
  canManageEmployees as checkCanManageEmployees,
  hasAdsAccess as checkHasAdsAccess,
  hasPriceComparisonAccess as checkHasPriceComparisonAccess,
  canDeleteShops as checkCanDeleteShops,
  canDeleteProducts as checkCanDeleteProducts,
  hasAdvancedMetrics as checkHasAdvancedMetrics,
  getMetricsLevel as getMetricsLevelUtil,
} from './utils';

const initialState: SubscriptionState = {
  subscription: null,
  currentShopsCount: 0,
  currentProductsCount: 0,
  isLoaded: false,
};

export const useSubscriptionStore = create<SubscriptionState & SubscriptionActions>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setSubscription: (subscription: CompanySubscription | null) => {
        set({ subscription, isLoaded: true });
      },

      setCurrentCounts: (shops: number, products: number) => {
        set({ currentShopsCount: shops, currentProductsCount: products });
      },

      setLoaded: (loaded: boolean) => {
        set({ isLoaded: loaded });
      },

      reset: () => {
        set(initialState);
      },

      // Feature checks
      canCreateShop: () => {
        const { subscription, currentShopsCount } = get();
        return checkCanCreateShop(subscription, currentShopsCount).canCreate;
      },

      canCreateProduct: () => {
        const { subscription, currentProductsCount } = get();
        return checkCanCreateProduct(subscription, currentProductsCount).canCreate;
      },

      canManageEmployees: () => {
        const { subscription } = get();
        return checkCanManageEmployees(subscription);
      },

      canAccessAds: () => {
        const { subscription } = get();
        return checkHasAdsAccess(subscription);
      },

      canAccessPriceComparison: () => {
        const { subscription } = get();
        return checkHasPriceComparisonAccess(subscription);
      },

      canDeleteShop: () => {
        const { subscription } = get();
        return checkCanDeleteShops(subscription);
      },

      canDeleteProduct: () => {
        const { subscription } = get();
        return checkCanDeleteProducts(subscription);
      },

      hasAdvancedMetrics: () => {
        const { subscription } = get();
        return checkHasAdvancedMetrics(subscription);
      },

      // Limits
      getShopsLimit: () => {
        const { subscription } = get();
        return subscription?.plan?.maxShops ?? PLAN_LIMITS.free.maxShops;
      },

      getProductsLimit: () => {
        const { subscription } = get();
        return subscription?.plan?.maxProducts ?? PLAN_LIMITS.free.maxProducts;
      },

      getMetricsLevel: () => {
        const { subscription } = get();
        return getMetricsLevelUtil(subscription);
      },

      getRemainingShops: () => {
        const { subscription, currentShopsCount } = get();
        const limit = subscription?.plan?.maxShops ?? PLAN_LIMITS.free.maxShops;
        return Math.max(0, limit - currentShopsCount);
      },

      getRemainingProducts: () => {
        const { subscription, currentProductsCount } = get();
        const limit = subscription?.plan?.maxProducts ?? PLAN_LIMITS.free.maxProducts;
        return Math.max(0, limit - currentProductsCount);
      },

      // Plan info
      getPlanTier: (): PlanTier => {
        const { subscription } = get();
        return getPlanTierFromSubscription(subscription);
      },

      getPlan: (): SubscriptionPlan | null => {
        const { subscription } = get();
        return subscription?.plan ?? null;
      },
    }),
    { ...devtoolsConfig, name: 'subscription-store' }
  )
);

// Selectors for optimized re-renders
export const selectSubscription = (state: SubscriptionState & SubscriptionActions) =>
  state.subscription;
export const selectIsLoaded = (state: SubscriptionState & SubscriptionActions) =>
  state.isLoaded;
export const selectCurrentCounts = (state: SubscriptionState & SubscriptionActions) => ({
  shops: state.currentShopsCount,
  products: state.currentProductsCount,
});
